import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSession, endSession, listSessionCatches, deleteSession } from '../api/sessions';
import { useAuth } from '../contexts/AuthContext';
import type { SessionResponse, CatchResponse } from '../types/api';
import { StatusBadge } from '../components/sessions/StatusBadge';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { initials } from '../lib/avatar';
import { CatchTable } from '../components/catches/CatchTable';
import { fromCatchResponse } from '../components/catches/catchRow';

export function SessionDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const from = (location.state as { from?: string } | null)?.from ?? '/sessions';
  const backLabel = from.startsWith('/clubs/') ? '← Club' : '← Sessions';

  const [session, setSession] = useState<SessionResponse | null>(null);
  const [catches, setCatches] = useState<CatchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [catchesLoading, setCatchesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function loadSession() {
    if (!id) return;
    try {
      const data = await getSession(Number(id));
      setSession(data);
    } catch {
      setError('Failed to load session.');
    } finally {
      setLoading(false);
    }
  }

  async function loadCatches() {
    if (!id) return;
    setCatchesLoading(true);
    try {
      const res = await listSessionCatches(Number(id), { pageSize: 100 });
      setCatches(res.items);
    } catch {
      // non-fatal
    } finally {
      setCatchesLoading(false);
    }
  }

  useEffect(() => {
    loadSession();
    loadCatches();
  }, [id]);

  async function handleEndSession() {
    if (!session || !window.confirm('End this session?')) return;
    setEnding(true);
    try {
      const updated = await endSession(session.id);
      setSession(updated);
    } catch {
      setError('Failed to end session.');
    } finally {
      setEnding(false);
    }
  }

  async function handleDeleteSession() {
    if (!session) return;
    setDeleting(true);
    try {
      await deleteSession(session.id);
      navigate('/sessions');
    } catch {
      setError('Failed to delete session.');
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  const isOwner = user != null && session != null && user.id === session.createdByUserId;
  const canDelete = isOwner || (user?.isSuperAdmin ?? false);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="h-8 w-32 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
        <div className="h-40 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700" />
        <div className="h-32 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <button
          onClick={() => navigate(from)}
          className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
        >
          {backLabel}
        </button>
        <p className="text-sm text-red-500">{error ?? 'Session not found.'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button
        onClick={() => navigate(from)}
        className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
      >
        {backLabel}
      </button>

      {/* Header */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">{session.name}</h1>
            <StatusBadge status={session.status} />
          </div>
          {(isOwner || canDelete) && (
            <div className="flex gap-2">
              {isOwner && session.status === 'Active' && (
                <button
                  onClick={() => navigate('/catches/log', { state: { sessionId: session.id } })}
                  className="bg-teal-600 dark:bg-teal-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  Log a catch
                </button>
              )}
              {isOwner && session.status === 'Active' && (
                <button
                  onClick={handleEndSession}
                  disabled={ending}
                  className="border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {ending ? 'Ending…' : 'End session'}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setDeleteOpen(true)}
                  className="border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg px-4 py-2 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-300">Spot:</span> {session.spotName}
          </span>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-300">Started:</span>{' '}
            {new Date(session.startTime).toLocaleString()}
          </span>
          {session.endTime ? (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              <span className="font-medium text-slate-700 dark:text-slate-300">Ended:</span>{' '}
              {new Date(session.endTime).toLocaleString()}
            </span>
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              <span className="font-medium text-slate-700 dark:text-slate-300">End:</span> ongoing
            </span>
          )}
          <span className="text-sm text-slate-500 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-300">Owner:</span>{' '}
            {session.createdByDisplayName}
          </span>
        </div>

        {/* Notes */}
        {session.notes && (
          <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 px-4 py-3">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 whitespace-pre-wrap">{session.notes}</p>
          </div>
        )}
      </div>

      {/* Participants */}
      {session.participants.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50 mb-3">Participants</h2>
          <div className="flex flex-wrap gap-2">
            {session.participants.map(p => {
              const label = initials(p.abbreviation, p.displayName);
              return (
                <div key={p.userId} className="flex items-center gap-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 dark:bg-teal-900/40 text-xs font-bold text-teal-700 dark:text-teal-400">
                    {label}
                  </span>
                  <span className="text-sm text-slate-700 dark:text-slate-300">{p.displayName}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete session?"
        description={`This will remove the session and hide its ${catches.length} catch${catches.length === 1 ? '' : 'es'}.`}
        confirmLabel="Delete"
        confirmTone="danger"
        busy={deleting}
        onConfirm={handleDeleteSession}
        onCancel={() => setDeleteOpen(false)}
      />

      {/* Catches */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50 mb-3">Catches</h2>
        {catchesLoading ? (
          <div className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
        ) : catches.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No catches logged yet.</p>
        ) : (
          <CatchTable rows={catches.map(fromCatchResponse)} columns={{ angler: true }} />
        )}
      </div>
    </div>
  );
}
